import React, { useContext, useEffect, useState, useRef } from 'react';
import {
    Box,
    Container,
    Heading,
    Text,
    VStack,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Button,
    useToast,
    Badge,
    HStack,
    Icon,
    Tabs,
    TabList,
    TabPanels,
    Tab,
    TabPanel,
    Grid,
    Spinner,
    Progress,
    Alert,
    AlertIcon,
    AlertDescription
} from '@chakra-ui/react';
import { FiUploadCloud, FiFile, FiTrash2 } from 'react-icons/fi';
import Navbar from '../compnents/Navbar';
import SideBar from '../compnents/SideBar';
import Footer from '../compnents/Footer';
import AuthContext from '../context/AuthContext';
import ThemeContext from '../context/ThemeContext';
import axios from 'axios';
import Loading from '../compnents/Loading';
import { useNavigate } from 'react-router-dom';
import FeatureUnlockContext from '../context/FeatureUnlockContext';
import UploadYourFile from './UploadYourFile';
import LiveJobIdLoader from '../compnents/LiveJobIdLoader';
import JobProgressContext from '../context/JobProgressContext';
import { FaShieldAlt } from 'react-icons/fa';

const isProduction = process.env.NODE_ENV === 'production';
const API_BASE_URL = isProduction
    ? process.env.REACT_APP_API_BASE_URL_PROD
    : process.env.REACT_APP_API_BASE_URL_DEV;

const Datasets = () => {
    const { token, user, setUser, logout } = useContext(AuthContext);
    const { themeData } = useContext(ThemeContext);
    const { resetUnlocks } = useContext(FeatureUnlockContext);
    const { jobStatus, showLiveLoader, triggerJobCheck } = useContext(JobProgressContext);
    const [files, setFiles] = useState([]);
    const [loadingUser, setLoadingUser] = useState(true);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);
    const [previewData, setPreviewData] = useState({});
    const [loadingPreview, setLoadingPreview] = useState({});
    const [deletingFileId, setDeletingFileId] = useState(null);

    // Extract theme colors with fallbacks
    const primaryColor = themeData?.colors?.primary || '#2B6CB0';
    const secondaryColor = themeData?.colors?.secondary || '#2C5282';
    const accentColor = themeData?.colors?.accent || '#63B3ED';
    const backgroundColor = themeData?.colors?.background || '#F7FAFC';

    // Determine if theme is dark (midnight)
    const isDarkTheme = themeData?.id === 'midnight';

    // Theme-aware colors
    const headingTextColor = isDarkTheme ? '#F7FAFC' : primaryColor;
    const bodyTextColor = isDarkTheme ? '#E2E8F0' : 'gray.600';
    const mutedTextColor = isDarkTheme ? '#CBD5E0' : 'gray.500';
    const labelTextColor = isDarkTheme ? '#E2E8F0' : 'gray.700';
    const strongTextColor = isDarkTheme ? '#F7FAFC' : 'gray.800';
    const cardBg = isDarkTheme ? 'rgba(45, 55, 72, 0.6)' : 'white';
    const infoBg = isDarkTheme ? 'rgba(66, 153, 225, 0.1)' : 'gray.50';
    const hoverBg = isDarkTheme ? 'rgba(66, 153, 225, 0.08)' : 'gray.50';
    const borderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.3)' : 'gray.200';
    const dashedBorderColor = isDarkTheme ? 'rgba(66, 153, 225, 0.5)' : 'gray.300';
    const uploadBorderColor = isDarkTheme ? accentColor : primaryColor;
    const buttonHoverBg = isDarkTheme ? accentColor : secondaryColor;
    const tabSelectedColor = isDarkTheme ? accentColor : primaryColor;
    const tableStripedBg = isDarkTheme ? 'rgba(66, 153, 225, 0.05)' : 'gray.50';

    // Upload states
    const [uploadFile, setUploadFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadError, setUploadError] = useState(null);

    const fileInputRef = useRef(null);
    const progressIntervalRef = useRef(null);
    const toast = useToast();
    const navigate = useNavigate();

    // Fetch all uploaded files
    const fetchFiles = async () => {
        try {
            const res = await axios.get(`${API_BASE_URL}/content/list-all-uploaded-files`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setFiles(res.data.files || []);
        } catch (error) {
            console.error('Error fetching files:', error);
            if (error.response?.status === 420) logout();
            toast({
                title: 'Failed to fetch files',
                description: error.response?.data?.message || 'Please try again.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchFiles();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        const fetchUser = async () => {
            if (!token || user) {
                setLoadingUser(false);
                return;
            }

            try {
                const res = await axios.get(`${API_BASE_URL}/auth/check-user`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                console.log("checking user: ", res);

                if (res.status === 420) {
                    console.error('Invalid token');
                    resetUnlocks();
                    logout();
                    return;
                }

                setUser(res.data);
            } catch (error) {
                console.error('Error fetching user:', error);
                if (error.response?.status === 420) {
                    console.error('Token expired or invalid, logging out...');
                    resetUnlocks();
                    logout();
                }
            } finally {
                setLoadingUser(false);
            }
        };

        fetchUser();
    }, [token, user, setUser, logout, resetUnlocks]);

    // Cleanup progress interval on unmount
    useEffect(() => {
        return () => {
            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }
        };
    }, []);

    if (!token || (!user && loadingUser)) {
        return (
            <Loading />
        );
    }

    if (!user) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" justifyContent="center" alignItems="center" bg={backgroundColor}>
                <Spinner size="xl" color={isDarkTheme ? accentColor : primaryColor} />
            </Box>
        );
    }

    if (user && user.noOfFileUploads === 0) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg={backgroundColor}>
                <Navbar />

                <UploadYourFile setUser={setUser} token={token} />
            </Box>
        )
    }

    // Fetch preview for a specific file
    const fetchPreview = async (fileId) => {
        if (previewData[fileId]) return; // Already loaded

        setLoadingPreview(prev => ({ ...prev, [fileId]: true }));
        try {
            const res = await axios.get(`${API_BASE_URL}/content/preview-file/${fileId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setPreviewData(prev => ({ ...prev, [fileId]: res.data }));
        } catch (error) {
            console.error('Error previewing file:', error);
            toast({
                title: 'Preview failed',
                description: error.response?.data?.message || 'Could not load preview.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setLoadingPreview(prev => ({ ...prev, [fileId]: false }));
        }
    };

    // When tab changes, fetch preview if needed
    const handleTabChange = (index) => {
        setActiveTab(index);
        if (index > 0 && index <= files.length) {
            const file = files[index - 1];
            fetchPreview(file.file_id);
        }
    };

    // Upload file handler
    const handleFileUpload = async (event) => {
        const selectedFiles = Array.from(event.target.files);
        const validFiles = selectedFiles.filter(
            (file) =>
                file.name.endsWith('.csv') ||
                file.name.endsWith('.xlsx') ||
                file.name.endsWith('.xls')
        );

        if (validFiles.length !== selectedFiles.length) {
            toast({
                title: 'Invalid file type',
                description: 'Please upload only CSV or Excel files',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }

        if (validFiles.length > 0) {
            const fileToUpload = validFiles[0];
            setUploadFile(fileToUpload);
            await uploadFileToAPI(fileToUpload);
        }
    };

    // Start artificial progress simulation
    const startProgressSimulation = () => {
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
        }

        let currentProgress = uploadProgress || 10;
        setUploadProgress(currentProgress);

        progressIntervalRef.current = setInterval(() => {
            setUploadProgress((prev) => {
                const increment = Math.random() * 1 + 2;
                const newProgress = prev + increment;

                if (newProgress >= 95) {
                    if (progressIntervalRef.current) {
                        clearInterval(progressIntervalRef.current);
                    }
                    return 95;
                }

                return newProgress;
            });
        }, 800);
    };

    // Complete the progress bar
    const completeProgress = () => {
        if (progressIntervalRef.current) {
            clearInterval(progressIntervalRef.current);
        }
        setUploadProgress(100);
    };

    // Upload to API
    const uploadFileToAPI = async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        try {
            setIsUploading(true);
            setUploadProgress(0);
            setUploadError(null);

            const response = await axios.post(`${API_BASE_URL}/content/upload-file`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    Authorization: `Bearer ${token}`
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round(
                        (progressEvent.loaded * 100) / progressEvent.total
                    );

                    if (percentCompleted < 100) {
                        setUploadProgress(Math.min(percentCompleted * 0.1, 10));
                    } else {
                        startProgressSimulation();
                    }
                }
            });

            completeProgress();

            const data = response.data;

            if (data.status === 'failed' && data.preview?.error) {
                setUploadError(data.preview.error);
                toast({
                    title: 'Upload validation failed',
                    description: data.preview.error,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            } else if (data.preview?.preview_rows && data.preview.preview_rows.length > 0) {
                toast({
                    title: 'File uploaded successfully',
                    description: 'Processing your dataset...',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });

                await fetchFiles();
                await triggerJobCheck();
            }
        } catch (error) {
            console.error('Upload error:', error);

            if (progressIntervalRef.current) {
                clearInterval(progressIntervalRef.current);
            }

            if (error.response?.status === 413) {
                const contentTooLargeMessage = 'Content too large. Max Limit 25MB.';
                setUploadError(contentTooLargeMessage);
                toast({
                    title: 'Upload failed',
                    description: contentTooLargeMessage,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            } else {
                const errorMessage = error.response?.data?.message ||
                    error.response?.data?.error ||
                    'Failed to upload file. Please try again.';

                setUploadError(errorMessage);
                toast({
                    title: 'Upload failed',
                    description: errorMessage,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        } finally {
            setIsUploading(false);
            setUploadFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
            window.location.reload();
        }
    };

    // Delete a file
    const handleDelete = async (fileId) => {
        if (!window.confirm('Are you sure you want to delete this file?')) return;

        setDeletingFileId(fileId);
        try {
            await axios.get(`${API_BASE_URL}/content/delete-file/${fileId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });

            toast({
                title: 'File deleted',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });

            setPreviewData(prev => {
                const newData = { ...prev };
                delete newData[fileId];
                return newData;
            });

            await fetchFiles();
            setActiveTab(0);
        } catch (error) {
            console.error('Error deleting file:', error);
            toast({
                title: 'Delete failed',
                description: error.response?.data?.message || 'Try again later.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setDeletingFileId(null);
        }
    };

    // Format file size
    const formatFileSize = (bytes) => {
        if (bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Drag and drop handlers
    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = async (e) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files);
        const validFiles = droppedFiles.filter(
            (file) =>
                file.name.endsWith('.csv') ||
                file.name.endsWith('.xlsx') ||
                file.name.endsWith('.xls')
        );

        if (validFiles.length > 0) {
            const fileToUpload = validFiles[0];
            setUploadFile(fileToUpload);
            await uploadFileToAPI(fileToUpload);
        }
    };

    if (loading) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg={backgroundColor}>
                <Navbar />
                <Box display="flex" flex="1">
                    <SideBar />
                    <Container maxW="container.xl" flex="1">
                        <Loading />
                    </Container>
                </Box>
                <Footer />
            </Box>
        );
    }

    if (jobStatus.loading) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg={backgroundColor}>
                <Navbar />
                <Box display="flex" flex="1">
                    <SideBar />
                    <Container maxW="container.xl" flex="1">
                        <Loading />
                    </Container>
                </Box>
                <Footer />
            </Box>
        );
    }

    if (showLiveLoader || jobStatus.hasIncompleteJobs) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column" bg={backgroundColor}>
                <Navbar />
                <Box display="flex" flex="1">
                    <SideBar />
                    <Box flex="1" display="flex" flexDirection="column">
                        <Container maxW="container.xl" pt={8} px={{ base: 4, md: 6 }}>
                            <VStack align="start" spacing={2} mb={4}>
                                <Heading as="h1" size="lg" color={headingTextColor} fontWeight="semibold">
                                    Processing Your Dataset
                                </Heading>
                                <Text color={bodyTextColor} fontSize="sm">
                                    Please wait while we process your data. This page will automatically update.
                                </Text>
                            </VStack>
                        </Container>
                        <LiveJobIdLoader />
                    </Box>
                </Box>
                <Footer />
            </Box>
        );
    }

    // Check if user has access
    const hasAccess = user.subscription === 'business' &&
        (user.role === 'admin' );

    if (!hasAccess) {
        return (
            <Box minH="100vh" display="flex" flexDirection="column">
                <Navbar />
                <Box display="flex" flex="1" bg={backgroundColor}>
                    <SideBar />
                    <Container maxW="container.xl" flex="1" py={8}>
                        <VStack spacing={6} align="center" justify="center" minH="60vh">
                            <Icon as={FaShieldAlt} boxSize={16} color={mutedTextColor} />
                            <Heading size="lg" color={headingTextColor}>
                                Datasets
                            </Heading>
                            <Text color={bodyTextColor} textAlign="center">
                                This feature is only available for Business subscription users with higher access than yours.
                            </Text>
                        </VStack>
                    </Container>
                </Box>
                <Footer />
            </Box>
        );
    }

    return (
        <Box minH="100vh" display="flex" flexDirection="column" bg={backgroundColor}>
            <Navbar />
            <Box display="flex" flex="1">
                <SideBar />

                <Container maxW="container.xl" flex="1" py={8} px={{ base: 4, md: 6 }} w="75vw" marginLeft={{ base: 10, md: 16 }}>
                    <VStack align="start" spacing={6} w="90%">
                        <Heading as="h1" size="lg" color={headingTextColor} fontWeight="semibold">
                            Your Datasets
                        </Heading>

                        <Box 
                            w="full" 
                            bg={cardBg} 
                            borderRadius="xl" 
                            boxShadow="md"
                            border="1px" 
                            borderColor={borderColor}
                        >
                            <Tabs index={activeTab} onChange={handleTabChange}>
                                <TabList 
                                    overflowX="auto" 
                                    overflowY="hidden" 
                                    flexWrap="nowrap"
                                    borderBottom="1px"
                                    borderColor={borderColor}
                                >
                                    {/* Upload Dataset Tab */}
                                    <Tab
                                        fontWeight="semibold"
                                        fontSize="sm"
                                        color={bodyTextColor}
                                        _selected={{ 
                                            color: tabSelectedColor, 
                                            borderColor: tabSelectedColor,
                                            borderBottomWidth: '2px'
                                        }}
                                        whiteSpace="nowrap"
                                        borderRight="1px solid"
                                        borderRightColor={borderColor}
                                    >
                                        <Icon as={FiUploadCloud} mr={2} />
                                        Upload Dataset
                                    </Tab>

                                    {/* File Tabs */}
                                    {files.map((file) => (
                                        <Tab
                                            key={file.file_id}
                                            fontWeight="medium"
                                            fontSize="sm"
                                            color={bodyTextColor}
                                            _selected={{ 
                                                color: tabSelectedColor, 
                                                borderColor: tabSelectedColor,
                                                borderBottomWidth: '2px'
                                            }}
                                            maxW="150px"
                                            borderRight="1px solid"
                                            borderRightColor={borderColor}
                                            mr={1}
                                        >
                                            <Icon as={FiFile} mr={2} />
                                            <Text isTruncated>{file.filename}</Text>
                                        </Tab>
                                    ))}
                                </TabList>

                                <TabPanels>
                                    {/* Upload Panel */}
                                    {user?.subscription === 'free' ? (
                                        <>
                                            <TabPanel p={6}>
                                                <Box 
                                                    textAlign="center" 
                                                    p={6} 
                                                    border="2px solid" 
                                                    borderColor={borderColor} 
                                                    borderRadius="lg" 
                                                    bg={infoBg}
                                                >
                                                    <Text fontSize="lg" mb={4} color={strongTextColor}>
                                                        You have already uploaded 1 file, purchase a subscription to upload more.
                                                    </Text>
                                                    <Button
                                                        bg={primaryColor}
                                                        color="white"
                                                        size="md"
                                                        onClick={() => navigate('/subscription')}
                                                        _hover={{ bg: buttonHoverBg }}
                                                    >
                                                        Go to Subscription Page
                                                    </Button>
                                                </Box>
                                            </TabPanel>
                                        </>
                                    ) : (
                                        <>
                                            <TabPanel p={6}>
                                                {/* Upload Area */}
                                                <Box
                                                    border="2px dashed"
                                                    borderColor={isUploading ? uploadBorderColor : dashedBorderColor}
                                                    borderRadius="lg"
                                                    p={12}
                                                    textAlign="center"
                                                    onDragOver={handleDragOver}
                                                    onDrop={handleDrop}
                                                    mb={4}
                                                    cursor={isUploading ? 'not-allowed' : 'pointer'}
                                                    transition="all 0.2s"
                                                    _hover={{ 
                                                        borderColor: uploadBorderColor, 
                                                        bg: hoverBg 
                                                    }}
                                                    onClick={() => !isUploading && fileInputRef.current?.click()}
                                                    opacity={isUploading ? 0.6 : 1}
                                                    pointerEvents={isUploading ? 'none' : 'auto'}
                                                >
                                                    {isUploading ? (
                                                        <Spinner 
                                                            size="xl" 
                                                            color={isDarkTheme ? accentColor : primaryColor} 
                                                            thickness="4px" 
                                                            mb={4} 
                                                        />
                                                    ) : (
                                                        <Icon 
                                                            as={FiUploadCloud} 
                                                            boxSize={10} 
                                                            color={isDarkTheme ? accentColor : mutedTextColor} 
                                                            mb={4} 
                                                        />
                                                    )}
                                                    <Text fontSize="md" mb={2} color={strongTextColor} fontWeight="medium">
                                                        {isUploading ? 'Uploading and Processing...' : 'Drag & drop your CSV/XLSX here'}
                                                    </Text>
                                                    {!isUploading && (
                                                        <>
                                                            <Text fontSize="sm" color={mutedTextColor} mb={4}>
                                                                or
                                                            </Text>
                                                            <Button 
                                                                bg={primaryColor} 
                                                                color="white" 
                                                                size="md" 
                                                                _hover={{ bg: buttonHoverBg }}
                                                            >
                                                                Browse files
                                                            </Button>
                                                            <Text fontSize="xs" color={mutedTextColor} mt={3}>
                                                                Accepted: .csv, .xlsx
                                                            </Text>
                                                        </>
                                                    )}
                                                    <input
                                                        ref={fileInputRef}
                                                        type="file"
                                                        accept=".csv,.xlsx,.xls"
                                                        onChange={handleFileUpload}
                                                        style={{ display: 'none' }}
                                                        disabled={isUploading}
                                                    />
                                                </Box>

                                                {/* Uploaded File Info */}
                                                {uploadFile && (
                                                    <HStack
                                                        bg={infoBg}
                                                        px={3}
                                                        py={2}
                                                        borderRadius="md"
                                                        spacing={2}
                                                        mb={4}
                                                        borderWidth="1px"
                                                        borderColor={borderColor}
                                                    >
                                                        <Icon as={FiFile} color={bodyTextColor} />
                                                        <Text fontSize="sm" fontWeight="medium" color={strongTextColor}>
                                                            {uploadFile.name}
                                                        </Text>
                                                        <Text fontSize="xs" color={mutedTextColor}>
                                                            • {(uploadFile.size / 1024).toFixed(0)}KB
                                                        </Text>
                                                    </HStack>
                                                )}

                                                {/* Upload Progress */}
                                                {isUploading && (
                                                    <Box mb={4}>
                                                        <HStack justify="space-between" mb={2}>
                                                            <Text fontSize="sm" fontWeight="medium" color={strongTextColor}>
                                                                {uploadProgress < 10 ? 'Uploading file...' : 'Processing your data...'}
                                                            </Text>
                                                            <Text fontSize="sm" fontWeight="bold" color={isDarkTheme ? accentColor : primaryColor}>
                                                                {Math.round(uploadProgress)}%
                                                            </Text>
                                                        </HStack>
                                                        <Progress
                                                            value={uploadProgress}
                                                            colorScheme="blue"
                                                            borderRadius="full"
                                                            size="sm"
                                                            hasStripe
                                                            isAnimated
                                                            sx={{
                                                                '& > div': {
                                                                    backgroundColor: isDarkTheme ? accentColor : primaryColor
                                                                }
                                                            }}
                                                        />
                                                        <Text fontSize="xs" color={bodyTextColor} mt={2}>
                                                            {uploadProgress < 10
                                                                ? 'Uploading your file to the server...'
                                                                : 'Analyzing and validating your data... This may take up to a minute.'}
                                                        </Text>
                                                    </Box>
                                                )}

                                                {/* Error Alert */}
                                                {uploadError && (
                                                    <Alert 
                                                        status="error" 
                                                        borderRadius="md" 
                                                        mb={4}
                                                        borderWidth="1px"
                                                        borderColor="red.300"
                                                    >
                                                        <AlertIcon />
                                                        <AlertDescription fontSize="sm">
                                                            {uploadError}
                                                        </AlertDescription>
                                                    </Alert>
                                                )}
                                            </TabPanel>
                                        </>
                                    )}

                                    {/* File Preview Panels */}
                                    {files.map((file) => (
                                        <TabPanel key={file.file_id} p={6}>
                                            {loadingPreview[file.file_id] ? (
                                                <Box textAlign="center" py={10}>
                                                    <Spinner 
                                                        size="xl" 
                                                        color={isDarkTheme ? accentColor : primaryColor} 
                                                        mb={4} 
                                                    />
                                                    <Text color={bodyTextColor}>Loading preview...</Text>
                                                </Box>
                                            ) : previewData[file.file_id] ? (
                                                <>
                                                    {/* File Info */}
                                                    <Box 
                                                        bg={infoBg} 
                                                        borderRadius="md" 
                                                        p={4} 
                                                        mb={4}
                                                        borderWidth="1px"
                                                        borderColor={borderColor}
                                                    >
                                                        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                                                            <Box>
                                                                <Text fontSize="xs" color={mutedTextColor}>Filename</Text>
                                                                <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                                                    {file.filename}
                                                                </Text>
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xs" color={mutedTextColor}>File ID</Text>
                                                                <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                                                    {file.file_id}
                                                                </Text>
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xs" color={mutedTextColor}>File Size</Text>
                                                                <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                                                    {formatFileSize(file.file_size)}
                                                                </Text>
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xs" color={mutedTextColor}>Status</Text>
                                                                <Badge
                                                                    colorScheme={
                                                                        file.job_status === 'completed' ? 'green' :
                                                                            file.job_status === 'failed' ? 'red' :
                                                                                file.job_status === 'processing' ? 'yellow' : 'blue'
                                                                    }
                                                                    fontSize="xs"
                                                                >
                                                                    {file.job_status}
                                                                </Badge>
                                                            </Box>
                                                        </Grid>
                                                    </Box>

                                                    {/* Preview Table */}
                                                    <Text fontSize="lg" fontWeight="semibold" mb={2} color={headingTextColor}>
                                                        Preview (first 5 rows)
                                                    </Text>
                                                    <Text fontSize="sm" fontWeight="normal" mb={4} color={bodyTextColor}>
                                                        Need to assign customers to RM? Visit the settings tab!
                                                    </Text>

                                                    <Box 
                                                        overflowX="auto" 
                                                        mb={6}
                                                        borderRadius="md"
                                                        borderWidth="1px"
                                                        borderColor={borderColor}
                                                    >
                                                        <Table variant="simple" size="sm">
                                                            <Thead bg={infoBg}>
                                                                <Tr>
                                                                    {previewData[file.file_id].columns_info?.map((col) => (
                                                                        <Th 
                                                                            key={col.name} 
                                                                            whiteSpace="nowrap" 
                                                                            fontSize="xs" 
                                                                            color={labelTextColor}
                                                                            borderColor={borderColor}
                                                                        >
                                                                            {col.name}
                                                                        </Th>
                                                                    ))}
                                                                </Tr>
                                                            </Thead>
                                                            <Tbody>
                                                                {previewData[file.file_id].preview_rows?.map((row, idx) => (
                                                                    <Tr 
                                                                        key={idx}
                                                                        bg={idx % 2 === 1 ? tableStripedBg : 'transparent'}
                                                                    >
                                                                        {previewData[file.file_id].columns_info?.map((col) => (
                                                                            <Td 
                                                                                key={col.name} 
                                                                                whiteSpace="nowrap" 
                                                                                fontSize="xs" 
                                                                                color={bodyTextColor}
                                                                                borderColor={borderColor}
                                                                            >
                                                                                {row[col.name]?.toString() || '-'}
                                                                            </Td>
                                                                        ))}
                                                                    </Tr>
                                                                ))}
                                                            </Tbody>
                                                        </Table>
                                                    </Box>

                                                    {/* Delete Button */}
                                                    <Button
                                                        colorScheme="red"
                                                        size="md"
                                                        leftIcon={<FiTrash2 />}
                                                        onClick={() => handleDelete(file.file_id)}
                                                        isLoading={deletingFileId === file.file_id}
                                                    >
                                                        Delete File
                                                    </Button>
                                                </>
                                            ) : (
                                                <Box textAlign="center">
                                                    {/* File Info */}
                                                    <Box 
                                                        bg={infoBg} 
                                                        borderRadius="md" 
                                                        p={4} 
                                                        mb={4}
                                                        borderWidth="1px"
                                                        borderColor={borderColor}
                                                    >
                                                        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
                                                            <Box>
                                                                <Text fontSize="xs" color={mutedTextColor}>Filename</Text>
                                                                <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                                                    {file.filename}
                                                                </Text>
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xs" color={mutedTextColor}>File ID</Text>
                                                                <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                                                    {file.file_id}
                                                                </Text>
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xs" color={mutedTextColor}>File Size</Text>
                                                                <Text fontSize="sm" fontWeight="semibold" color={strongTextColor}>
                                                                    {formatFileSize(file.file_size)}
                                                                </Text>
                                                            </Box>
                                                            <Box>
                                                                <Text fontSize="xs" color={mutedTextColor}>Status</Text>
                                                                <Badge
                                                                    colorScheme={
                                                                        file.job_status === 'completed' ? 'green' :
                                                                            file.job_status === 'failed' ? 'red' :
                                                                                file.job_status === 'processing' ? 'yellow' : 'blue'
                                                                    }
                                                                    fontSize="xs"
                                                                >
                                                                    {file.job_status}
                                                                </Badge>
                                                            </Box>
                                                        </Grid>
                                                    </Box>
                                                    <Text color={bodyTextColor} my={10}>No preview available :(</Text>
                                                    {/* Delete Button */}
                                                    <Button
                                                        colorScheme="red"
                                                        size="md"
                                                        leftIcon={<FiTrash2 />}
                                                        onClick={() => handleDelete(file.file_id)}
                                                        isLoading={deletingFileId === file.file_id}
                                                    >
                                                        Delete File
                                                    </Button>
                                                </Box>
                                            )}
                                        </TabPanel>
                                    ))}
                                </TabPanels>
                            </Tabs>
                        </Box>
                    </VStack>
                </Container >
            </Box >
            <Footer />
        </Box >
    );
};

export default Datasets;